import { internalData } from "../../../store/store.ts";
import { iconPath } from "../../../data/icons.ts";
import '../../overlays/as-tooltip-wrapper.ts';
import '../../text-and-lang/as-text.ts';
import './as-button.ts'; // For the confirmation action button
import { CONFIRM_ON_CLICK_ATTRS, confirmOnClickSheets, wrapWithConfirm, attachConfirmListener } from './confirmOnClick.ts';
import styleText from './as-icon-button.css?raw';
import { AsButtonBase } from './as-button-base.ts';
/**
 * # AsIconButton Component
 * 
 * A reusable icon button component that provides a consistent UI for controls
 * across the application suite. Features a customizable icon button with tooltip support.
 * 
 * ## Features
 * - Icon-based button with hover effects
 * - Integrated tooltip with customizable placement
 * - Support for light/dark icon variants
 * - Emits events when clicked
 * - Fully customizable via attributes
 * - Supports both direct filenames and icon names from ICON_LIST
 * 
 * ## Component Hierarchy
 * ```
 * AsIconButton (as-icon-button)
 * ├── button - The clickable button element
 * │   └── img - The icon image
 * └── as-tooltip - Tooltip component that appears on hover (optional)
 * ```
 * 
 * ## Attributes
 * - `file-name`: Direct name of the icon image file
 * - `abs-img-path`: Absolute path to the icon image file (highest priority)
 * - `icon-set`: Name of the icon set from ICON_LIST
 * - `icon-variant`: Variant of the icon from ICON_LIST (default: 'default')
 * - `button-id`: Optional ID for the button element
 * - `black`: When present, applies dark styling to the icon (filter: brightness(0))
 * - `tooltip-default-text`: Default text content for the tooltip
 * - `tooltip-dict-key`: Localization key for tooltip text
 * - `tooltip-placement`: Position of the tooltip (top, right, bottom, left, etc.)
 * - `name`: Optional name for the button, used for event identification // button-key  button-id
 * - `no-default-icon`: Stops `name` from picking an icon from ICON_LIST
 * - `disable-tooltips`: When present, prevents tooltips from showing
 * 
 * ## CSS Custom Properties
 * - `--icon-btn-padding`: Vertical padding (default: 0.375rem)
 * - `--icon-btn-radius`: Border radius (default: 0.5rem)
 * - `--icon-btn-size`: Button height and width (default: 2.5rem)
 * - `--icon-btn-bg-col`: Hover background color (default: #002738)
 * - `--icon-btn-hover-bg-col`: Custom hover background color (default: var(--icon-btn-bg-col, #002738))
 * - `--icon-btn-img-filter`: Optional filter to apply to the image on hover (default: none)
 * 
 * ## Events
 * ### Emitted Events
 * - `as-button-click`: Dispatched when the button is clicked
 *   - Detail: `{ buttonName: string | null }` - The name of the button if set
 *   - Expected detail: `{ buttonName: string, iconVariant: string }`
 * 
 * ## Usage Examples
 * 
 * ### Basic Usage with Direct Filename
 * ```html
 * <as-icon-button 
 *   file-name="home.png" 
 *   tooltip-default-text="Go to Home">
 * </as-icon-button>
 * ```
 * 
 * ### With Absolute Image Path
 * ```html
 * <as-icon-button 
 *   abs-img-path="/path/to/images/home-icon.png" 
 *   tooltip-default-text="Go to Home">
 * </as-icon-button>
 * ```
 * 
 * ### With Icon Set
 * ```html
 * <as-icon-button 
 *   icon-set="toggle-workpiece-view"
 *   icon-variant="default"
 *   tooltip-default-text="Toggle Workpiece">
 * </as-icon-button>
 * ```
 * 
 * ### With Custom Tooltip Placement
 * ```html
 * <as-icon-button 
 *   file-name="settings.png" 
 *   tooltip-default-text="Settings" 
 *   tooltip-placement="bottom-right">
 * </as-icon-button>
 * ```
 * 
 * ### Dark Icon Variant
 * ```html
 * <as-icon-button 
 *   file-name="help.png" 
 *   tooltip-default-text="Help" 
 *   black>
 * </as-icon-button>
 * ```
 * 
 * ### With Event Name
 * ```html
 * <as-icon-button 
 *   file-name="save.png" 
 *   tooltip-default-text="Save"
 *   name="save">
 * </as-icon-button>
 * ```
 * 
 * ### Handling Click Events
 * ```javascript
 * const iconButton = document.querySelector('as-icon-button');
 * iconButton.addEventListener('as-button-click', (e) => {
 *   devlog('Button clicked!', e.detail.buttonName);
 *   // Handle navigation or action
 * });
 * ```
 * 
 
 * 
 * ## Implementation Notes
 * - Uses shadow DOM for encapsulation
 * - Automatically shows/hides tooltip on pointer enter/leave
 * - Icon images should be designed for both light and dark backgrounds
 * - When the `black` attribute is present and the button is hovered, 
 *   the image filter can be customized with the `--icon-btn-img-filter` CSS variable
 * - Supports both direct filenames and icon sets from ICON_LIST
 * - Prioritizes icon resolution: 1) abs-img-path, 2) icon-set & variant, 3) name attribute (unless no-default-icon), 4) direct file-name
 */

/**
 * Base button component for header controls
 */
export class AsIconButton extends AsButtonBase {
  static readonly tag = 'as-icon-button' as const;

  static get observedAttributes() {
    return [
      'file-name', 'abs-img-path', 'icon-set', 'icon-variant', 'toggle-icon-variant',
      'button-id', 'black', 'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement',
      'name', 'disable-tooltips', 'tooltip-flash', 'tooltip-flash-duration', 'tooltip-flash-delay',
      'toggle', ...CONFIRM_ON_CLICK_ATTRS,
      'disabled', 'no-default-icon',
    ];
  }

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _getIconFileName(): string {
    const absImgFilePath = this.getAttribute('abs-img-path');
    const iconSet = this.getAttribute('icon-set');
    const toggleIconVariant = this.getAttribute('toggle-icon-variant');
    const iconVariant = (this.toggledOn && toggleIconVariant)
      ? toggleIconVariant
      : (this.getAttribute('icon-variant') || 'default');
    const fileName = this.getAttribute('file-name');
    const buttonName = this.getAttribute('name');

    // In priority order: an explicit path, the named icon set, the name
    // attribute used as a set, then a bare file name.
    if (absImgFilePath) return absImgFilePath;

    const fromSet = iconPath(iconSet, iconVariant, true);
    if (fromSet) return fromSet;

    if (!iconSet && !this.hasAttribute('no-default-icon')) {
      const fromName = iconPath(buttonName, iconVariant);
      if (fromName) return fromName;
    }

    if (fileName) return `${internalData.imgDir}/${fileName}`;

    return '';
  }
  
  /**
   * Check if tooltip should be rendered
   */
  private _hasTooltip(): boolean {
    const dictKey = this.getAttribute('tooltip-dict-key');
    const defaultText = this.getAttribute('tooltip-default-text');
    const tooltipsDisabled = this.hasAttribute('disable-tooltips');
    return (!!dictKey || !!defaultText) && !tooltipsDisabled;
  }

  /**
   * Get tooltip wrapper attributes string
   */
  private _getTooltipWrapperAttrs(): string {
    const dictKey = this.getAttribute('tooltip-dict-key');
    const defaultText = this.getAttribute('tooltip-default-text');
    const placement = this.getAttribute('tooltip-placement') ?? 'bottom';
    const flash = this.getAttribute('tooltip-flash');
    const flashDuration = this.getAttribute('tooltip-flash-duration');
    const flashDelay = this.getAttribute('tooltip-flash-delay');

    const attrs: string[] = ['show-on-hover', `placement="${placement}"`];
    if (dictKey) attrs.push(`dict-key="${dictKey}"`);
    if (defaultText) attrs.push(`default-text="${defaultText}"`);
    if (flash !== null) attrs.push('flash');
    if (flashDuration) attrs.push(`flash-duration="${flashDuration}"`);
    if (flashDelay) attrs.push(`flash-delay="${flashDelay}"`);

    return attrs.join(' ');
  }

  protected _syncToggleState(): void {
    this._applyToggle();
  }

  private _applyToggle(): void {
    const button = this.shadowRoot?.querySelector('button');
    const img = this.shadowRoot?.querySelector('img');
    if (!button) return;
    if (this.hasAttribute('toggle')) {
      button.classList.toggle('on', this.toggledOn);
      if (img) img.src = this._getIconFileName();
    } else {
      button.classList.remove('on');
      this.toggledOn = false;
    }
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, ...confirmOnClickSheets, AsIconButton._sheet];
    this._render();
  }

  private _render(): void {
    const buttonHTML = /*html*/`<button${this.hasAttribute('disabled') ? ' disabled' : ''}><img src="${this._getIconFileName()}" /></button>`;
    
    let content = buttonHTML;

    if (this._hasTooltip()) {
      content = /*html*/`
        <as-tooltip-wrapper ${this._getTooltipWrapperAttrs()}>
          ${content}
        </as-tooltip-wrapper>
      `;
    }

    content = wrapWithConfirm(this, content);

    this.shadowRoot!.innerHTML = content;
    this._attachInternalListeners();
  }

  connectedCallback() {
    if (this.getAttribute('toggle') === 'on') {
      this.toggledOn = true;
      this._applyToggle();
    }
    this._attachInternalListeners();
  }

  private _attachInternalListeners(): void {
    const button = this.shadowRoot!.querySelector('button');
    button?.removeEventListener('click', this._handleButtonClick);
    button?.addEventListener('click', this._handleButtonClick);

    attachConfirmListener(this, () => this._dispatchClick());
  }

  private _handleButtonClick = (e: MouseEvent) => {
    if (this.hasAttribute('confirm-on-click')) {
      // as-hovercard-wrapper handles the click to show confirmation
      return;
    }

    if (this.hasAttribute('toggle')) {
      this.toggledOn = !this.toggledOn;
      this._applyToggle();
    }
    this._dispatchClick(e);
  };

  private _dispatchClick(originalEvent?: MouseEvent) {
    this._dispatchButtonClick({ 
      buttonName: this.getAttribute('name'), 
      toggledOn: this.toggledOn,
      originalEvent 
    });
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (!this.shadowRoot || oldValue === newValue) return;

    if (name.startsWith('confirm-')) {
      this._render();
      return;
    }

    const img = this.shadowRoot.querySelector('img');
    const button = this.shadowRoot.querySelector('button');
    const wrapper = this.shadowRoot.querySelector('as-tooltip-wrapper');

    // Handle icon-related attributes
    if (name === 'file-name' || name === 'abs-img-path' || name === 'icon-set' || name === 'icon-variant' || name === 'name' || name === 'no-default-icon') {
      if (img) img.src = this._getIconFileName();
    }

    if (name === 'disabled') {
      const btn = this.shadowRoot?.querySelector('button');
      if (btn) newValue !== null ? btn.setAttribute('disabled', '') : btn.removeAttribute('disabled');
      return;
    }

    if (name === 'toggle') {
      this._applyToggle();
      return;
    }

    if (name === 'button-id' && button) {
      button.id = newValue;
    }

    // Handle disable-tooltips - requires re-render to add/remove wrapper
    if (name === 'disable-tooltips') {
      this._render();
      return;
    }

    // Forward tooltip attributes to wrapper
    if (wrapper) {
      if (name === 'tooltip-default-text') {
        wrapper.setAttribute('default-text', newValue ?? '');
      } else if (name === 'tooltip-dict-key') {
        wrapper.setAttribute('dict-key', newValue ?? '');
      } else if (name === 'tooltip-placement') {
        wrapper.setAttribute('placement', newValue ?? 'bottom');
      } else if (name === 'tooltip-flash') {
        if (newValue !== null) {
          wrapper.setAttribute('flash', '');
        } else {
          wrapper.removeAttribute('flash');
        }
      } else if (name === 'tooltip-flash-duration') {
        wrapper.setAttribute('flash-duration', newValue ?? '');
      } else if (name === 'tooltip-flash-delay') {
        wrapper.setAttribute('flash-delay', newValue ?? '');
      }
    }
  }
}

customElements.define(AsIconButton.tag, AsIconButton); 
